import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '../../hooks/use-toast.js';
import { buildCanonicalVisitItems, canonicalVisitItemValidationMessage, confirmedPaymentsTotal, createRequestKey, customerBillableTotal, isVisitFinalized, normalizeVisitCardState, normalizeVisitPayment, paymentMethodLabelMap, roundMoney, validateCanonicalVisitItems, visitDisplayTotal, visitRemainingBalance, } from './vehicleVisitCardModel.js';
import { vehicleVisitCardApi, vehicleVisitErrorMessage } from './vehicleVisitCardApi.js';
const activeStatuses = new Set(['in_progress', 'diagnosis', 'quotation', 'approved', 'waiting_approval', 'waiting_for_parts', 'repair', 'quality_check', 'ready', 'delivering']);
const startsEditable = (visit, finalized, archiveMode) => !finalized
    && !archiveMode
    && activeStatuses.has(String(visit?.status || 'in_progress').toLowerCase());

export const useVehicleVisitCard = ({ visit, vehicle, archiveMode = false, onUpdate, onVisitClosed, onShowWhatsAppPreview, onAuditEvent, }) => {
    const { toast } = useToast();
    const initial = useMemo(() => normalizeVisitCardState(visit), [visit]);
    const finalized = isVisitFinalized(visit);
    const [isExpanded, setIsExpanded] = useState(activeStatuses.has(String(visit.status || 'in_progress').toLowerCase()));
    const [items, setItems] = useState(initial.items);
    const [payments, setPayments] = useState(initial.payments);
    const [status, setStatus] = useState(initial.status);
    const [techId, setTechId] = useState(initial.technicianId);
    const [mileage, setMileage] = useState(initial.mileage);
    const [notes, setNotes] = useState(initial.notes);
    const [isEditing, setIsEditing] = useState(startsEditable(visit, finalized, archiveMode));
    const [isSaving, setIsSaving] = useState(false);
    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
    const [finalizeConfirmOpen, setFinalizeConfirmOpen] = useState(false);
    const [confirmPayOpen, setConfirmPayOpen] = useState(false);
    const [confirmPayLoading, setConfirmPayLoading] = useState(false);
    const [advancePayOpen, setAdvancePayOpen] = useState(false);
    const [advancePayLoading, setAdvancePayLoading] = useState(false);
    const [whatsappNotification, setWhatsappNotification] = useState(null);
    const activeVisitIdRef = useRef(visit.id);
    const saveRequestKeyRef = useRef(null);
    const closeRequestKeyRef = useRef(null);
    const paymentAttemptRef = useRef(null);
    const advancePaymentAttemptRef = useRef(null);
    const whatsappNotificationRef = useRef(null);
    const finalizeRequestKeyRef = useRef(null);
    useEffect(() => {
        const next = normalizeVisitCardState(visit);
        const isFinalized = isVisitFinalized(visit);
        const visitChanged = String(activeVisitIdRef.current) !== String(visit.id);
        setItems(next.items);
        setPayments(next.payments);
        setStatus(next.status);
        setTechId(next.technicianId);
        setMileage(next.mileage);
        setNotes(next.notes);
        if (visitChanged) {
            activeVisitIdRef.current = visit.id;
            saveRequestKeyRef.current = null;
            closeRequestKeyRef.current = null;
            paymentAttemptRef.current = null;
            advancePaymentAttemptRef.current = null;
            setAdvancePayOpen(false);
            setCloseConfirmOpen(false);
            setFinalizeConfirmOpen(false);
            whatsappNotificationRef.current = null;
            finalizeRequestKeyRef.current = null;
            setWhatsappNotification(null);
            setIsEditing(startsEditable(visit, isFinalized, archiveMode));
        }
        else if (isFinalized || archiveMode) {
            setIsEditing(false);
        }
        if (whatsappNotificationRef.current)
            setWhatsappNotification(whatsappNotificationRef.current);
    }, [visit, archiveMode]);
    const totalAmount = useMemo(() => visitDisplayTotal(visit, items), [visit, items]);
    const paidTotal = useMemo(() => confirmedPaymentsTotal(payments), [payments]);
    const remainingBalance = useMemo(() => visitRemainingBalance(visit, items, payments), [visit, items, payments]);
    const savePayload = useCallback((nextStatus = status) => ({
        status: nextStatus,
        technicianId: techId || null,
        mileage: mileage === '' || mileage === null ? null : Number(mileage),
        notesText: notes,
        items: buildCanonicalVisitItems(items),
    }), [items, mileage, notes, status, techId]);
    const hasUnsavedFinancialChanges = useMemo(() => {
        // Invalid financial input stays visible for validation; never serialize
        // a reference record merely to compare editor state.
        if (validateCanonicalVisitItems(items) || validateCanonicalVisitItems(initial.items)) return true;
        return JSON.stringify(savePayload()) !== JSON.stringify({
            status: initial.status, technicianId: initial.technicianId || null,
            mileage: initial.mileage === '' || initial.mileage === null ? null : Number(initial.mileage),
            notesText: initial.notes, items: buildCanonicalVisitItems(initial.items),
        });
    }, [initial, items, savePayload]);
    const dispatchRefreshEvents = useCallback((source, detail = {}) => {
        try {
            const vehicleId = visit.vehicleId || visit.vehicle_id || vehicle?.id;
            window.dispatchEvent(new CustomEvent('finance:updated', {
                detail: { source, visitId: visit.id, vehicleId, ...detail },
            }));
            window.dispatchEvent(new CustomEvent('vehicles:updated', {
                detail: { source, vehicleId },
            }));
            window.dispatchEvent(new CustomEvent('archive:updated', {
                detail: { source, vehicleId },
            }));
        }
        catch (error) {
            console.warn('vehicle visit refresh event failed', error);
        }
    }, [vehicle?.id, visit.id, visit.vehicleId, visit.vehicle_id]);
    const handleSave = useCallback(async () => {
        if (isSaving || finalized)
            return;
        const validation = validateCanonicalVisitItems(items);
        if (validation) {
            toast({ title: 'لا يمكن الحفظ', description: canonicalVisitItemValidationMessage(validation), variant: 'destructive' });
            return;
        }
        setIsSaving(true);
        saveRequestKeyRef.current || (saveRequestKeyRef.current = createRequestKey('visit-save', visit.id));
        try {
            await vehicleVisitCardApi.save(visit.id, {
                ...savePayload(),
                requestKey: saveRequestKeyRef.current,
            });
            saveRequestKeyRef.current = null;
            setIsEditing(false);
            dispatchRefreshEvents('visit_items_save', { itemsCount: items.length, totalAmount });
            onAuditEvent?.({
                action: 'visit_save',
                actionLabel: 'حفظ تعديل زيارة',
                visitId: visit.id,
                status,
                itemsCount: items.length,
                totalAmount,
            });
            onUpdate?.();
            toast({ title: 'تم الحفظ', description: `تم حفظ ${items.length} بند بنجاح. يمكنك متابعة تعديل البنود حتى اعتماد الإجمالي النهائي.` });
            return true;
        }
        catch (error) {
            toast({ title: 'خطأ في الحفظ', description: vehicleVisitErrorMessage(error, 'فشل الحفظ. تأكد من الاتصال وحاول مرة أخرى.'), variant: 'destructive' });
            return false;
        }
        finally {
            setIsSaving(false);
        }
    }, [dispatchRefreshEvents, finalized, isSaving, items, items.length, onAuditEvent, onUpdate, savePayload, status, toast, totalAmount, visit.id]);
    const requestCloseVisit = useCallback(() => {
        if (isSaving)
            return;
        if (!finalized) {
            const validation = validateCanonicalVisitItems(items);
            if (validation) {
                toast({ title: 'لا يمكن الحفظ', description: canonicalVisitItemValidationMessage(validation), variant: 'destructive' });
                return;
            }
        }
        setCloseConfirmOpen(true);
    }, [finalized, isSaving, items, toast]);
    const confirmCloseVisit = useCallback(async () => {
        if (isSaving)
            return;
        if (!finalized) {
            const validation = validateCanonicalVisitItems(items);
            if (validation) {
                setCloseConfirmOpen(false);
                toast({ title: 'لا يمكن الحفظ', description: canonicalVisitItemValidationMessage(validation), variant: 'destructive' });
                return;
            }
        }
        setIsSaving(true);
        closeRequestKeyRef.current || (closeRequestKeyRef.current = createRequestKey('visit-close', visit.id));
        try {
            const response = await vehicleVisitCardApi.close(visit.id, finalized
                ? { requestKey: closeRequestKeyRef.current }
                : { ...savePayload('completed'), requestKey: closeRequestKeyRef.current });
            closeRequestKeyRef.current = null;
            setStatus('completed');
            setIsEditing(false);
            setCloseConfirmOpen(false);
            const whatsappUrl = response?.data?.whatsappNotificationUrl || response?.data?.whatsapp_notification_url;
            if (whatsappUrl) {
                const customerName = vehicle?.customerName || vehicle?.customer_name || 'العميل';
                let message = '';
                try {
                    const url = new URL(whatsappUrl);
                    message = decodeURIComponent(url.searchParams.get('text') || '');
                }
                catch {
                    message = '';
                }
                const notification = { url: whatsappUrl, customerName, message };
                whatsappNotificationRef.current = notification;
                setWhatsappNotification(notification);
                onShowWhatsAppPreview?.(notification);
                onVisitClosed?.(notification);
            }
            dispatchRefreshEvents('visit_close', { itemsCount: items.length, totalAmount });
            onAuditEvent?.({
                action: 'visit_close',
                actionLabel: 'حفظ وإغلاق زيارة',
                visitId: visit.id,
                itemsCount: items.length,
                totalAmount,
            });
            onUpdate?.();
            toast({ title: 'تم الحفظ والإغلاق', description: 'تم حفظ البنود وإغلاق الزيارة بنجاح' });
        }
        catch (error) {
            toast({ title: 'خطأ في الإغلاق', description: vehicleVisitErrorMessage(error, 'فشل إغلاق الزيارة. تأكد من الاتصال وحاول مرة أخرى.'), variant: 'destructive' });
        }
        finally {
            setIsSaving(false);
        }
    }, [dispatchRefreshEvents, finalized, isSaving, items, items.length, onAuditEvent, onShowWhatsAppPreview, onUpdate, onVisitClosed, savePayload, toast, totalAmount, vehicle?.customerName, vehicle?.customer_name, visit.id]);
    const handleReopen = useCallback(async () => {
        if (finalized) {
            toast({ title: 'غير مسموح', description: 'الزيارة المعتمدة ماليًا لا يمكن إعادة فتحها.', variant: 'destructive' });
            return;
        }
        try {
            await vehicleVisitCardApi.reopen(visit.id);
            setStatus('in_progress');
            setIsEditing(true);
            setIsExpanded(true);
            onAuditEvent?.({ action: 'visit_reopen', actionLabel: 'إعادة فتح زيارة للتعديل', visitId: visit.id });
            onUpdate?.();
            toast({ title: 'تم', description: 'تم إعادة فتح الزيارة للتعديل' });
        }
        catch (error) {
            toast({ title: 'خطأ', description: vehicleVisitErrorMessage(error, 'فشل إعادة فتح الزيارة'), variant: 'destructive' });
        }
    }, [finalized, onAuditEvent, onUpdate, toast, visit.id]);
    const requestFinalize = useCallback(() => {
        if (isSaving || finalized)
            return;
        if (isEditing) {
            toast({ title: 'احفظ البنود أولًا', description: 'احفظ آخر تعديل قبل اعتماد الإجمالي النهائي.', variant: 'destructive' });
            return;
        }
        const validation = validateCanonicalVisitItems(items);
        if (validation) {
            toast({ title: 'لا يمكن الاعتماد', description: canonicalVisitItemValidationMessage(validation), variant: 'destructive' });
            return;
        }
        setFinalizeConfirmOpen(true);
    }, [finalized, isEditing, isSaving, items, toast]);
    const confirmFinalize = useCallback(async () => {
        if (isSaving || finalized)
            return;
        const validation = validateCanonicalVisitItems(items);
        if (validation) {
            setFinalizeConfirmOpen(false);
            toast({ title: 'لا يمكن الاعتماد', description: canonicalVisitItemValidationMessage(validation), variant: 'destructive' });
            return;
        }
        const expectedTotal = roundMoney(customerBillableTotal(items));
        setIsSaving(true);
        finalizeRequestKeyRef.current || (finalizeRequestKeyRef.current = createRequestKey('visit-finalize', visit.id));
        try {
            await vehicleVisitCardApi.finalize(visit.id, {
                expectedTotal,
                idempotencyKey: finalizeRequestKeyRef.current,
            });
            setFinalizeConfirmOpen(false);
            dispatchRefreshEvents('visit_finalize', { totalAmount: expectedTotal });
            onAuditEvent?.({
                action: 'visit_finalize',
                actionLabel: 'اعتماد إجمالي نهائي للزيارة',
                visitId: visit.id,
                totalAmount: expectedTotal,
            });
            onUpdate?.();
            toast({
                title: 'تم اعتماد الإجمالي',
                description: expectedTotal === 0
                    ? 'تم اعتماد زيارة لا تحمل مبلغًا على العميل.'
                    : 'تم اعتماد الإجمالي النهائي، وأي رصيد لاحق يظهر لصالح الورشة.',
            });
        }
        catch (error) {
            toast({ title: 'خطأ في الاعتماد', description: vehicleVisitErrorMessage(error, 'فشل اعتماد الإجمالي النهائي.'), variant: 'destructive' });
        }
        finally {
            setIsSaving(false);
        }
    }, [dispatchRefreshEvents, finalized, isSaving, items, onAuditEvent, onUpdate, toast, visit.id]);
    const addItem = useCallback((requestedType) => {
        if (finalized)
            return;
        setItems((current) => [
            ...current,
            {
                id: undefined,
                clientKey: createRequestKey('visit-item', visit.id),
                itemType: requestedType === 'supplier' ? 'supplier' : 'service',
                name: '',
                quantity: 1,
                qty: 1,
                price: 0,
                total: 0,
                customerBillable: true,
            },
        ]);
    }, [finalized, visit.id]);
    const updateItem = useCallback((index, field, value) => {
        if (finalized)
            return;
        setItems((current) => current.map((item, itemIndex) => {
            if (itemIndex !== index)
                return item;
            const next = { ...item, [field]: value };
            if (field === 'price' || field === 'quantity' || field === 'qty') {
                // Keep an entered zero visible so validation can reject it. Turning
                // it back into one would silently change the operation on save.
                const quantity = Number(next.quantity ?? next.qty ?? 1);
                const price = Number(next.price ?? 0);
                next.quantity = quantity;
                next.qty = quantity;
                next.total = roundMoney(quantity * price);
            }
            return next;
        }));
    }, [finalized]);
    const deleteItem = useCallback((index) => {
        if (finalized)
            return;
        setItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
    }, [finalized]);
    const cancelEditing = useCallback(() => {
        const next = normalizeVisitCardState(visit);
        setItems(next.items);
        setPayments(next.payments);
        setStatus(next.status);
        setTechId(next.technicianId);
        setMileage(next.mileage);
        setNotes(next.notes);
        setIsEditing(false);
    }, [visit]);
    const paymentAttemptSignature = useCallback((lines, date) => JSON.stringify({
        visitId: visit.id,
        date,
        lines: lines.map((line) => ({
            method: line.method,
            amount: roundMoney(line.amount),
            reference: line.reference || '',
        })),
    }), [visit.id]);
    const handleConfirmVisitPayment = useCallback(async ({ paymentLines = [], date, discount = 0 }) => {
        if (!finalized || roundMoney(remainingBalance) <= 0) {
            toast({
                title: 'لا توجد ذمة مستحقة',
                description: 'يمكن تحصيل العميل بعد اعتماد الزيارة فقط وبحد أقصى الرصيد المستحق.',
                variant: 'destructive',
            });
            return;
        }
        if (Number(discount || 0) > 0) {
            toast({
                title: 'الخصم غير متاح هنا',
                description: 'الخصم يحتاج حدثًا ماليًا معتمدًا مستقلًا، ولا يمكن إسقاطه من الذمة ضمن عملية التحصيل.',
                variant: 'destructive',
            });
            return;
        }
        const explicitTotal = roundMoney(paymentLines.reduce((sum, line) => {
            const amount = Number(line?.amount);
            return sum + (Number.isFinite(amount) && amount > 0 ? roundMoney(amount) : 0);
        }, 0));
        const emptyLines = paymentLines.filter((line) => !Number(line?.amount));
        const autoFillRemaining = Math.max(0, roundMoney(remainingBalance - explicitTotal));
        const autoShare = emptyLines.length > 0 ? roundMoney(autoFillRemaining / emptyLines.length) : 0;
        const validLines = paymentLines
            .map((line) => ({
            ...line,
            amount: Number(line?.amount) > 0 ? roundMoney(line.amount) : autoShare,
        }))
            .filter((line) => line.amount > 0);
        if (!validLines.length) {
            toast({ title: 'تنبيه', description: 'يرجى إدخال مبلغ صحيح', variant: 'destructive' });
            return;
        }
        const disallowed = validLines.find((line) => line.method === 'supplier_balance');
        if (disallowed) {
            toast({
                title: 'وسيلة غير مدعومة',
                description: 'رصيد المورد ليس وسيلة تحصيل عميل داخل الزيارة.',
                variant: 'destructive',
            });
            return;
        }
        const paymentDate = date || new Date().toISOString().split('T')[0];
        const signature = paymentAttemptSignature(validLines, paymentDate);
        if (!paymentAttemptRef.current || paymentAttemptRef.current.signature !== signature) {
            const batchId = createRequestKey('visit-payment', visit.id);
            paymentAttemptRef.current = {
                signature,
                keys: validLines.map((_, index) => `${batchId}:${index + 1}`),
            };
        }
        setConfirmPayLoading(true);
        try {
            const responses = [];
            for (let index = 0; index < validLines.length; index += 1) {
                const line = validLines[index];
                const key = paymentAttemptRef.current.keys[index];
                const response = await vehicleVisitCardApi.recordPayment(visit.id, {
                    amount: line.amount,
                    method: line.method || 'cash',
                    date: paymentDate,
                    reference: line.reference || '',
                }, key);
                responses.push(response?.data || {});
            }
            paymentAttemptRef.current = null;
            const returnedPayments = responses
                .map((entry, index) => entry.payment ? normalizeVisitPayment(entry.payment, payments.length + index, visit.id) : null)
                .filter(Boolean);
            if (returnedPayments.length)
                setPayments((current) => [...current, ...returnedPayments]);
            setConfirmPayOpen(false);
            const totalConfirmed = roundMoney(validLines.reduce((sum, line) => sum + line.amount, 0));
            dispatchRefreshEvents('visit_payment', { amount: totalConfirmed });
            // تحصيل العميل يحدّث ذمة الزيارة فقط. التسليم والأرشفة لهما دورة مستقلة.
            onUpdate?.();
            toast({
                title: 'تم السداد',
                description: validLines.map((line) => `${paymentMethodLabelMap[line.method] || line.method}: ${line.amount.toLocaleString('ar-SA')} ر.س`).join(' • '),
            });
        }
        catch (error) {
            toast({ title: 'خطأ', description: vehicleVisitErrorMessage(error, 'فشل تسجيل السداد'), variant: 'destructive' });
        }
        finally {
            setConfirmPayLoading(false);
        }
    }, [dispatchRefreshEvents, finalized, onUpdate, paymentAttemptSignature, payments.length, remainingBalance, toast, visit.id]);
    const handleConfirmVisitAdvancePayment = useCallback(async ({ paymentLines = [], date }) => {
        const cancelled = String(status || visit.status || '').toLowerCase() === 'cancelled';
        if (!finalized || cancelled || roundMoney(remainingBalance) !== 0) {
            toast({
                title: 'الدفعة المقدمة غير متاحة',
                description: 'تُسجل الدفعة المقدمة فقط بعد اعتماد الزيارة عندما يكون رصيدها صفرًا.',
                variant: 'destructive',
            });
            return;
        }
        const validLines = paymentLines.map((line) => ({
            ...line,
            amount: roundMoney(line?.amount),
        }));
        if (!validLines.length || validLines.some((line) => line.amount <= 0)) {
            toast({
                title: 'مبلغ غير صحيح',
                description: 'أدخل مبلغًا موجبًا صريحًا لكل وسيلة دفع.',
                variant: 'destructive',
            });
            return;
        }
        if (validLines.some((line) => line.method === 'supplier_balance')) {
            toast({
                title: 'وسيلة غير مدعومة',
                description: 'الدفعة المقدمة لصالح العميل لا تُسجل من رصيد المورد.',
                variant: 'destructive',
            });
            return;
        }
        const paymentDate = date || new Date().toISOString().split('T')[0];
        const signature = JSON.stringify({
            visitId: visit.id,
            flow: 'advance',
            date: paymentDate,
            lines: validLines.map((line) => ({
                method: line.method,
                amount: roundMoney(line.amount),
                reference: line.reference || '',
            })),
        });
        if (!advancePaymentAttemptRef.current || advancePaymentAttemptRef.current.signature !== signature) {
            const batchId = createRequestKey('visit-advance-payment', visit.id);
            advancePaymentAttemptRef.current = {
                signature,
                keys: validLines.map((_, index) => `${batchId}:${index + 1}`),
            };
        }
        setAdvancePayLoading(true);
        try {
            const responses = [];
            for (let index = 0; index < validLines.length; index += 1) {
                const line = validLines[index];
                const key = advancePaymentAttemptRef.current.keys[index];
                const response = await vehicleVisitCardApi.recordPayment(visit.id, {
                    amount: line.amount,
                    method: line.method || 'cash',
                    date: paymentDate,
                    reference: line.reference || '',
                }, key);
                responses.push(response?.data || {});
            }
            advancePaymentAttemptRef.current = null;
            const returnedPayments = responses
                .map((entry, index) => entry.payment ? normalizeVisitPayment(entry.payment, payments.length + index, visit.id) : null)
                .filter(Boolean);
            if (returnedPayments.length)
                setPayments((current) => [...current, ...returnedPayments]);
            setAdvancePayOpen(false);
            const totalConfirmed = roundMoney(validLines.reduce((sum, line) => sum + line.amount, 0));
            dispatchRefreshEvents('visit_advance_payment', { amount: totalConfirmed, advance: true });
            onUpdate?.();
            toast({
                title: 'تم تسجيل الدفعة المقدمة',
                description: `سُجلت دفعة مقدمة لصالح العميل بقيمة ${totalConfirmed.toLocaleString('ar-SA')} ر.س، ولا تُخفض ذمة الزيارة الحالية.`,
            });
        }
        catch (error) {
            toast({ title: 'خطأ', description: vehicleVisitErrorMessage(error, 'فشل تسجيل الدفعة المقدمة'), variant: 'destructive' });
        }
        finally {
            setAdvancePayLoading(false);
        }
    }, [dispatchRefreshEvents, finalized, onUpdate, payments.length, remainingBalance, status, toast, visit.id, visit.status]);
    const dismissWhatsappNotification = useCallback(() => {
        whatsappNotificationRef.current = null;
        setWhatsappNotification(null);
    }, []);
    return {
        finalized,
        hasUnsavedFinancialChanges,
        isExpanded,
        setIsExpanded,
        items,
        setItems,
        payments,
        status,
        techId,
        setTechId,
        mileage,
        setMileage,
        notes,
        setNotes,
        isEditing,
        setIsEditing,
        cancelEditing,
        isSaving,
        closeConfirmOpen,
        setCloseConfirmOpen,
        finalizeConfirmOpen,
        setFinalizeConfirmOpen,
        confirmPayOpen,
        setConfirmPayOpen,
        confirmPayLoading,
        advancePayOpen,
        setAdvancePayOpen,
        advancePayLoading,
        whatsappNotification,
        dismissWhatsappNotification,
        totalAmount,
        paidTotal,
        remainingBalance,
        addItem,
        updateItem,
        deleteItem,
        handleSave,
        requestCloseVisit,
        confirmCloseVisit,
        handleReopen,
        requestFinalize,
        confirmFinalize,
        handleConfirmVisitPayment,
        handleConfirmVisitAdvancePayment,
    };
};
